import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpCircle, Copy, Eraser, ExternalLink, History, KeyRound, Palette, Play, Plus, Puzzle, RefreshCw, RotateCcw, SlidersHorizontal, Terminal, Upload } from "lucide-react";
import { useState } from "react";
import type { SiteTools } from "@hostdock/sdk";
import type { WebsiteDetail, WordPressExtension, WordPressInfo } from "@hostdock/types";
import { Badge, StatusBadge } from "../badge";
import { TechLogo } from "../tech-logo";
import { Button } from "../button";
import { formatBytes } from "../chart";
import { ConfirmDialog, Dialog } from "../dialog";
import { EmptyState, ErrorState, FormError, Notice } from "../feedback";
import { Checkbox, Field, Input, Select, Switch } from "../form";
import { formatDateTime } from "../format";
import { ActionMenu } from "../menu";
import { OneTimeSecret } from "../secret";
import { toast } from "../toast";
import { InfoTile, TabCard, tabPrimaryClass, tabSecondaryClass } from "./tab-card";

/** Opens a single-use URL in a new tab (opened first so popup blockers allow it). */
async function openFresh(get: () => Promise<{ url: string }>) {
  const tab = window.open("about:blank", "_blank");
  try {
    const { url } = await get();
    if (tab) {
      tab.opener = null;
      tab.location.href = url;
    } else window.location.href = url;
  } catch (err) {
    tab?.close();
    toast.error(err instanceof Error ? err.message : "Could not open WordPress");
  }
}

/**
 * WordPress Manager. Everything runs WP-CLI inside the website's own container
 * as the site user; long operations (updates, copies, push, restore) run as
 * background jobs and appear under Deployments.
 */
export function WordPressManager({ w, tools, canManage, onJob, onRedis, onOpenWebsite }: {
  w: WebsiteDetail;
  tools: SiteTools;
  canManage: boolean;
  /** Called when a background job was started (e.g. to show Deployments). */
  onJob?(jobId: string): void;
  /** Enables or disables the website's Redis user (the object cache follows automatically). */
  onRedis?(enabled: boolean): Promise<unknown>;
  onOpenWebsite?(id: string): void;
}) {
  const qc = useQueryClient();
  const key = ["wordpress", w.id];
  const installing = w.status === "PROVISIONING" || w.status === "PENDING";
  const q = useQuery({ queryKey: key, queryFn: () => tools.wordpress.get(w.id), enabled: w.status === "ACTIVE", retry: false, refetchInterval: 30_000 });
  const refresh = () => void qc.invalidateQueries({ queryKey: key });
  const [password, setPassword] = useState<{ username: string; password: string } | null>(null);
  const settings = useMutation({
    mutationFn: (input: Parameters<SiteTools["wordpress"]["settings"]>[1]) => tools.wordpress.settings(w.id, input),
    onSuccess: () => { toast.success("Saved"); refresh(); },
    onError: (e) => toast.error((e as Error).message),
  });
  const purge = useMutation({ mutationFn: () => tools.wordpress.purgeCache(w.id), onSuccess: () => toast.success("Cache purged"), onError: (e) => toast.error((e as Error).message) });
  const reset = useMutation({ meta: { inlineError: true }, mutationFn: () => tools.wordpress.resetPassword(w.id), onSuccess: setPassword });
  const update = useMutation({
    mutationFn: (scope: "core" | "plugins" | "themes" | "all") => tools.wordpress.update(w.id, scope),
    onSuccess: (r) => { toast.success("Update started; a rollback point is saved first"); onJob?.(r.job.id); },
    onError: (e) => toast.error((e as Error).message),
  });
  const redis = useMutation({
    mutationFn: (enabled: boolean) => onRedis!(enabled),
    onSuccess: () => { toast.success("Redis updated; WordPress is being reconfigured"); setTimeout(refresh, 8000); },
    onError: (e) => toast.error((e as Error).message),
  });

  if (installing) return <Notice title="Installing WordPress">WordPress is being installed. This page fills in when it is ready.</Notice>;
  if (w.status === "SUSPENDED") return <Notice tone="warning" title="This website is suspended">WordPress cannot be managed while the website is suspended.</Notice>;
  if (w.status !== "ACTIVE") return <EmptyState title="WordPress is available once the website is running" />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const info = q.data;
  if (!info) return <p className="flex items-center gap-2 text-[13px] text-muted"><RefreshCw size={14} className="animate-spin" />Reading WordPress…</p>;
  const manage = canManage;

  const pluginUpdates = info.plugins.filter((i) => i.update).length;
  const themeUpdates = info.themes.filter((i) => i.update).length;
  const sw = "rounded-lg border border-line bg-surface-2/40 px-[12px] py-[11px]";
  return (
    <div className="flex flex-col gap-4">
      <TabCard logo={<TechLogo tech="wordpress" size="sm" />} title="WordPress"
        description={info.url ? <a href={info.url} target="_blank" rel="noreferrer" className="text-accent hover:underline">{info.url}</a> : "Site address not reported yet"}
        action={manage && <button type="button" className={tabPrimaryClass} onClick={() => void openFresh(() => tools.wordpress.login(w.id))}><ExternalLink size={15} />Log in to WordPress</button>}>
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          <InfoTile label="Version" mono={false} value={<span className="flex flex-wrap items-center gap-2">{info.version ?? "—"}{info.coreUpdate && <Badge tone="warning">{info.coreUpdate} available</Badge>}</span>} />
          <InfoTile label="PHP" mono={false} value={info.phpVersion} />
          <InfoTile label="HTTPS" mono={false} value={info.ssl ? <Badge tone="success">Active</Badge> : <Badge>Waiting for DNS</Badge>} />
          <InfoTile label="Administrator" value={info.adminUsername} copy={info.adminUsername} />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-muted">
          <span>{info.plugins.length} plugin{info.plugins.length === 1 ? "" : "s"}{pluginUpdates ? ` · ${pluginUpdates} with updates` : ""}</span>
          <span>{info.themes.length} theme{info.themes.length === 1 ? "" : "s"}{themeUpdates ? ` · ${themeUpdates} with updates` : ""}</span>
          {info.woocommerce && <span className="flex items-center gap-1.5">WooCommerce <Badge tone="success">Active</Badge></span>}
        </div>
        {manage && (
          <div className="mt-4 flex flex-wrap gap-2 border-t border-line pt-4">
            {info.coreUpdate && <button type="button" className={tabPrimaryClass} disabled={update.isPending} onClick={() => update.mutate("core")}>{update.isPending && update.variables === "core" ? <RefreshCw size={14} className="animate-spin" /> : <ArrowUpCircle size={15} />}Update WordPress to {info.coreUpdate}</button>}
            <button type="button" className={tabSecondaryClass} disabled={update.isPending} onClick={() => update.mutate("all")}>{update.isPending && update.variables === "all" ? <RefreshCw size={14} className="animate-spin" /> : <ArrowUpCircle size={15} />}Update everything</button>
            <button type="button" className={tabSecondaryClass} disabled={purge.isPending} onClick={() => purge.mutate()}>{purge.isPending ? <RefreshCw size={14} className="animate-spin" /> : <Eraser size={15} />}Purge cache</button>
            <button type="button" className={tabSecondaryClass} disabled={reset.isPending} onClick={() => reset.mutate()}>{reset.isPending ? <RefreshCw size={14} className="animate-spin" /> : <KeyRound size={15} />}Reset administrator password</button>
          </div>
        )}
        {reset.error ? <div className="mt-3"><FormError error={reset.error} /></div> : null}
      </TabCard>

      <TabCard icon={SlidersHorizontal} title="Settings" description="Changes apply to WordPress straight away.">
        <div className="grid gap-2 lg:grid-cols-2">
          <div className={sw}><Switch checked={info.maintenance} disabled={!manage || settings.isPending} onChange={(v) => settings.mutate({ maintenance: v })} label="Maintenance mode" description="Visitors see a short maintenance message; signed-in administrators are not affected." /></div>
          <div className={sw}><Switch checked={info.debug} disabled={!manage || settings.isPending} onChange={(v) => settings.mutate({ debug: v })} label="Debug mode" description="Logs PHP notices and errors to wp-content/debug.log (never shown to visitors)." /></div>
          <div className={sw}><Switch checked={info.searchEngineVisible} disabled={!manage || settings.isPending} onChange={(v) => settings.mutate({ searchEngineVisible: v })} label="Visible to search engines" description="When off, WordPress asks search engines not to index the site." /></div>
          {onRedis && (
            <div className={sw}>
              <Switch checked={info.redis.enabled} disabled={!manage || redis.isPending} onChange={(v) => redis.mutate(v)} label="Redis object cache"
                description={info.redis.enabled ? (info.redis.objectCache ? "Active: database queries are cached in the website's private Redis store." : "Redis is on; WordPress is being connected to it.") : "Caches database queries in a private Redis store."} />
            </div>
          )}
        </div>
        <div className="mt-4 grid gap-3 border-t border-line pt-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="WordPress updates">
            {(p) => (
              <Select {...p} value={info.autoUpdates.core} disabled={!manage || settings.isPending} onChange={(e) => settings.mutate({ autoUpdates: { core: e.target.value as "minor" } })}>
                <option value="minor">Security and minor releases</option>
                <option value="major">All releases</option>
                <option value="off">Off</option>
              </Select>
            )}
          </Field>
          <div className="flex items-end pb-2"><Checkbox checked={info.autoUpdates.plugins} disabled={!manage || settings.isPending} onChange={(v) => settings.mutate({ autoUpdates: { plugins: v } })} label="Auto-update plugins" /></div>
          <div className="flex items-end pb-2"><Checkbox checked={info.autoUpdates.themes} disabled={!manage || settings.isPending} onChange={(v) => settings.mutate({ autoUpdates: { themes: v } })} label="Auto-update themes" /></div>
        </div>
      </TabCard>

      <Extensions kind="plugin" items={info.plugins} w={w} tools={tools} canManage={manage} onChange={refresh} onUpdateAll={() => update.mutate("plugins")} />
      <Extensions kind="theme" items={info.themes} w={w} tools={tools} canManage={manage} onChange={refresh} onUpdateAll={() => update.mutate("themes")} />
      {manage && <WpCli w={w} tools={tools} />}
      <StagingPanel w={w} info={info} tools={tools} canManage={manage} onJob={onJob} onOpenWebsite={onOpenWebsite} />
      <RollbackPoints w={w} tools={tools} canManage={manage} onJob={onJob} />

      <Dialog open={password !== null} onOpenChange={(o) => !o && setPassword(null)} title="New administrator password" footer={<Button variant="primary" onClick={() => setPassword(null)}>Done</Button>}>
        {password && <OneTimeSecret label={`Password for ${password.username}`} value={password.password} note="Copy it now; HostDock does not store it. You can always sign in with Log in to WordPress instead." />}
      </Dialog>
    </div>
  );
}

function Extensions({ kind, items, w, tools, canManage, onChange, onUpdateAll }: {
  kind: "plugin" | "theme"; items: WordPressExtension[]; w: WebsiteDetail; tools: SiteTools; canManage: boolean; onChange(): void; onUpdateAll(): void;
}) {
  const [installing, setInstalling] = useState(false);
  const [deleting, setDeleting] = useState<WordPressExtension | null>(null);
  const label = kind === "plugin" ? "Plugins" : "Themes";
  const act = useMutation({
    mutationFn: (v: { action: "install" | "activate" | "deactivate" | "update" | "delete" | "auto-update-on" | "auto-update-off"; slug: string; activate?: boolean }) => tools.wordpress.extension(w.id, kind, v),
    onSuccess: (_r, v) => { toast.success(`${v.slug}: done`); setDeleting(null); onChange(); },
    onError: (e) => toast.error((e as Error).message),
  });
  const updates = items.filter((i) => i.update).length;
  return (
    <TabCard icon={kind === "plugin" ? Puzzle : Palette} title={label} count={items.length} flush
      description={updates ? `${updates} update${updates === 1 ? "" : "s"} available. A rollback point is saved before updating.` : `All ${label.toLowerCase()} are up to date.`}
      action={canManage && (
        <>
          {updates > 0 && <button type="button" className={tabSecondaryClass} onClick={onUpdateAll}><ArrowUpCircle size={15} />Update {updates}</button>}
          <button type="button" className={tabSecondaryClass} onClick={() => setInstalling(true)}><Plus size={15} />Add {kind}</button>
        </>
      )}>
      {items.length === 0 ? <EmptyState title={`No ${label.toLowerCase()}`} /> : (
        <ul className="divide-y divide-line">
          {items.map((i) => {
            const on = i.status === "active" || i.status === "parent";
            return (
              <li key={i.name} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-[16px] py-[11px]">
                <span className={on ? "flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent" : "flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-lg bg-surface-3 text-muted"} aria-hidden>
                  {kind === "plugin" ? <Puzzle size={15} /> : <Palette size={15} />}
                </span>
                <div className="min-w-0 flex-[1_1_14rem] leading-tight">
                  <p className="truncate text-[13.5px] font-semibold">{i.title}</p>
                  <p className="mt-[3px] truncate font-mono text-[11.5px] text-muted">{i.name}</p>
                </div>
                <span className="flex items-center gap-2 text-[12.5px] tabular-nums">
                  <span className="rounded-md bg-surface-3 px-[7px] py-[2px] font-mono text-[11.5px]">{i.version}</span>
                  {i.update && <Badge tone="warning">{i.update}</Badge>}
                </span>
                {/* Must-use plugins and drop-ins are always loaded by WordPress; they cannot be switched off here. */}
                {i.status === "must-use" ? <Badge tone="info">Must-use</Badge> : i.status === "dropin" ? <Badge tone="info">Drop-in</Badge> : <StatusBadge status={on ? "ACTIVE" : "INACTIVE"} />}
                <span className="hidden text-[12px] text-subtle sm:inline">Auto-update {i.autoUpdate ? "on" : "off"}</span>
                {canManage && i.status !== "must-use" && i.status !== "dropin" ? (
                  <ActionMenu
                    label={`Actions for ${i.name}`}
                    actions={[
                      { label: "Activate", hidden: on, onSelect: () => act.mutate({ action: "activate", slug: i.name }) },
                      { label: "Deactivate", hidden: kind === "theme" || i.status !== "active", onSelect: () => act.mutate({ action: "deactivate", slug: i.name }) },
                      { label: `Update to ${i.update}`, hidden: !i.update, onSelect: () => act.mutate({ action: "update", slug: i.name }) },
                      { label: i.autoUpdate ? "Turn off auto-update" : "Turn on auto-update", onSelect: () => act.mutate({ action: i.autoUpdate ? "auto-update-off" : "auto-update-on", slug: i.name }) },
                      { label: "Delete", danger: true, hidden: on, onSelect: () => setDeleting(i) },
                    ]}
                  />
                ) : <span className="w-8" aria-hidden />}
              </li>
            );
          })}
        </ul>
      )}
      {installing && (
        <InstallDialog kind={kind} loading={act.isPending} error={act.error} onClose={() => setInstalling(false)}
          onInstall={(slug, activate) => act.mutate({ action: "install", slug, activate }, { onSuccess: () => setInstalling(false) })} />
      )}
      <ConfirmDialog open={deleting !== null} onOpenChange={(o) => !o && setDeleting(null)} title={`Delete ${deleting?.title}?`} description="Its files are removed from the website." confirmLabel="Delete" loading={act.isPending} error={act.error} onConfirm={() => deleting && act.mutate({ action: "delete", slug: deleting.name })} />
    </TabCard>
  );
}

function InstallDialog({ kind, loading, error, onClose, onInstall }: { kind: "plugin" | "theme"; loading: boolean; error: unknown; onClose(): void; onInstall(slug: string, activate: boolean): void }) {
  const [slug, setSlug] = useState("");
  const [activate, setActivate] = useState(true);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Add ${kind}`} size="sm"
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" loading={loading} disabled={!/^[a-z0-9][a-z0-9._-]*$/.test(slug)} onClick={() => onInstall(slug, activate)}>Install</Button></>}>
      <div className="flex flex-col gap-4">
        <Field label={`${kind === "plugin" ? "Plugin" : "Theme"} slug`} hint={`From the wordpress.org URL, e.g. ${kind === "plugin" ? "wordpress.org/plugins/contact-form-7 → contact-form-7" : "wordpress.org/themes/astra → astra"}`}>
          {(p) => <Input {...p} value={slug} onChange={(e) => setSlug(e.target.value.trim().toLowerCase())} className="font-mono" autoFocus />}
        </Field>
        <Checkbox checked={activate} onChange={setActivate} label="Activate after installing" />
        {error ? <FormError error={error} /> : null}
      </div>
    </Dialog>
  );
}

function WpCli({ w, tools }: { w: WebsiteDetail; tools: SiteTools }) {
  const [command, setCommand] = useState("");
  const [history, setHistory] = useState<{ command: string; exitCode: number; output: string }[]>([]);
  const run = useMutation({ meta: { inlineError: true },
    mutationFn: (c: string) => tools.wordpress.cli(w.id, c),
    onSuccess: (r, c) => { setHistory((h) => [{ command: c, ...r }, ...h].slice(0, 20)); setCommand(""); },
  });
  return (
    <TabCard icon={Terminal} title="WP-CLI" description="Runs as this website's user inside its own container. No shell: one wp command at a time, e.g. plugin list or user list.">
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (command.trim()) run.mutate(command.trim()); }}>
        <div className="flex h-[38px] flex-1 items-center rounded-lg bg-[#0f1f2c] px-[12px] font-mono text-[13px] text-[#cfe3ec] focus-within:ring-2 focus-within:ring-accent">
          <span className="mr-2 text-[#5fd3c7]">$</span>
          <span className="mr-1.5 text-[#8aa4b4]">wp</span>
          <input aria-label="WP-CLI command" value={command} onChange={(e) => setCommand(e.target.value)} className="h-full min-w-0 flex-1 bg-transparent text-[#e6f1f5] outline-none placeholder:text-[#5b7383]" placeholder="option get blogname" spellCheck={false} />
        </div>
        <button type="submit" className={tabPrimaryClass} disabled={run.isPending || !command.trim()}>{run.isPending ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}Run</button>
      </form>
      {run.error ? <div className="mt-3"><FormError error={run.error} /></div> : null}
      {history.length > 0 && (
        <div className="mt-3 flex max-h-[28rem] flex-col gap-2.5 overflow-auto">
          {history.map((h, i) => (
            <div key={i} className="overflow-hidden rounded-lg bg-[#0f1f2c]">
              <p className="flex items-center justify-between border-b border-white/10 px-[12px] py-[7px] font-mono text-[12px] text-[#cfe3ec]">
                <span>$ wp {h.command.replace(/^wp\s+/, "")}</span>
                <span className={h.exitCode === 0 ? "text-[#5fd3a0]" : "text-[#ff8a8a]"}>exit {h.exitCode}</span>
              </p>
              <pre className="max-h-72 overflow-auto px-[12px] py-[10px] font-mono text-[11.5px] leading-[18px] whitespace-pre-wrap text-[#cfe3ec]">{h.output || "(no output)"}</pre>
            </div>
          ))}
        </div>
      )}
    </TabCard>
  );
}

function StagingPanel({ w, info, tools, canManage, onJob, onOpenWebsite }: {
  w: WebsiteDetail; info: WordPressInfo; tools: SiteTools; canManage: boolean; onJob?(id: string): void; onOpenWebsite?(id: string): void;
}) {
  const [creating, setCreating] = useState<"staging" | "clone" | null>(null);
  const [domain, setDomain] = useState("");
  const [pushing, setPushing] = useState(false);
  const copy = useMutation({ meta: { inlineError: true },
    mutationFn: () => (creating === "staging" ? tools.wordpress.createStaging(w.id, domain) : tools.wordpress.clone(w.id, domain)),
    onSuccess: (r) => { toast.success(creating === "staging" ? "Creating the staging copy" : "Creating the copy"); setCreating(null); onOpenWebsite?.(r.websiteId); },
  });
  const push = useMutation({ meta: { inlineError: true },
    mutationFn: (confirm: string) => tools.wordpress.pushStaging(w.id, confirm),
    onSuccess: (r) => { toast.success("Pushing to live; a rollback point of the live site is saved first"); setPushing(false); onJob?.(r.job.id); },
  });
  const live = info.staging.liveSite;
  return (
    <TabCard icon={Copy} title="Staging and copies" description="Copies are made on the same server: files, database and every URL inside WordPress."
      action={canManage && !live && (
        <>
          {!info.staging.copies.length && <button type="button" className={tabSecondaryClass} onClick={() => { setDomain(`staging.${w.primaryDomain}`); setCreating("staging"); }}><Plus size={15} />Create staging copy</button>}
          <button type="button" className={tabSecondaryClass} onClick={() => { setDomain(""); setCreating("clone"); }}><Copy size={14} />Clone to another domain</button>
        </>
      )}>
      {live ? (
        <div className="flex flex-col gap-3">
          <Notice title="This is a staging copy">
            It was copied from <button type="button" className="font-medium text-accent hover:underline" onClick={() => onOpenWebsite?.(live.id)}>{live.domain}</button> and is hidden from search engines.
          </Notice>
          {canManage && <div><button type="button" className={tabPrimaryClass} onClick={() => setPushing(true)}><Upload size={15} />Push to live</button></div>}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {info.staging.copies.length > 0 ? (
            <ul className="divide-y divide-line rounded-lg border border-line">
              {info.staging.copies.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center gap-2.5 px-[12px] py-[9px] text-[13px]">
                  <Copy size={15} className="shrink-0 text-subtle" />
                  <button type="button" className="font-semibold text-accent hover:underline" onClick={() => onOpenWebsite?.(c.id)}>{c.domain}</button>
                  <span className="rounded-md bg-surface-3 px-[7px] py-[1px] text-[10.5px] font-medium tracking-wide text-muted uppercase">Staging</span>
                  <span className="ml-auto"><StatusBadge status={c.status} /></span>
                </li>
              ))}
            </ul>
          ) : <p className="text-[13px] text-muted">No staging copy yet. Try changes on a copy first, then push them to live.</p>}
        </div>
      )}
      <Dialog open={creating !== null} onOpenChange={(o) => !o && setCreating(null)} title={creating === "staging" ? "Create staging copy" : "Clone website"} size="sm"
        footer={<><Button variant="ghost" onClick={() => setCreating(null)}>Cancel</Button><Button variant="primary" loading={copy.isPending} disabled={!domain} onClick={() => copy.mutate()}>Create</Button></>}>
        <div className="flex flex-col gap-3">
          <Field label="Domain for the copy" hint="Counts as a website on your plan. Point its DNS like any other domain to get HTTPS.">
            {(p) => <Input {...p} value={domain} onChange={(e) => setDomain(e.target.value.trim().toLowerCase())} placeholder="staging.example.com" autoFocus />}
          </Field>
          {copy.error ? <FormError error={copy.error} /> : null}
        </div>
      </Dialog>
      {live && (
        <ConfirmDialog open={pushing} onOpenChange={setPushing} title={`Replace ${live.domain} with this staging copy?`}
          description={`Files and the database of ${live.domain} are replaced with this copy. A rollback point of the live site is saved first, so you can restore it from the live site's WordPress page.`}
          confirmLabel="Push to live" confirmText={live.domain} loading={push.isPending} error={push.error} onConfirm={() => push.mutate(live.domain)} />
      )}
    </TabCard>
  );
}

function RollbackPoints({ w, tools, canManage, onJob }: { w: WebsiteDetail; tools: SiteTools; canManage: boolean; onJob?(id: string): void }) {
  const q = useQuery({ queryKey: ["wp-snapshots", w.id], queryFn: () => tools.wordpress.snapshots(w.id), refetchInterval: 30_000 });
  const [restoring, setRestoring] = useState<string | null>(null);
  const restore = useMutation({ meta: { inlineError: true },
    mutationFn: (id: string) => tools.wordpress.restore(w.id, id, w.primaryDomain),
    onSuccess: (r) => { toast.success("Restoring"); setRestoring(null); onJob?.(r.job.id); },
  });
  return (
    <TabCard icon={History} title="Rollback points" count={q.data?.items.length} flush description="Saved automatically before updates and before staging is pushed to live. The last 3 are kept on the server. These are not backups."
      action={<button type="button" className={tabSecondaryClass} onClick={() => void q.refetch()} aria-label="Refresh" title="Refresh"><RefreshCw size={14} className={q.isFetching ? "animate-spin" : undefined} /></button>}>
      {q.error ? <ErrorState error={q.error} onRetry={() => void q.refetch()} className="m-3" />
        : q.isPending ? <p className="px-[16px] py-5 text-[13px] text-muted">Loading…</p>
        : !q.data?.items.length ? <EmptyState icon={History} title="No rollback points yet" description="One is saved automatically before the next update." />
        : (
          <ul className="divide-y divide-line">
            {q.data.items.map((sn) => (
              <li key={sn.id} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-[16px] py-[11px]">
                <span className="flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-lg bg-surface-3 text-muted" aria-hidden><History size={15} /></span>
                <div className="min-w-0 flex-[1_1_14rem] leading-tight">
                  <p className="truncate text-[13.5px] font-semibold">{sn.reason}</p>
                  <p className="mt-[3px] text-[12px] text-muted">{formatDateTime(sn.createdAt)}{sn.sizeBytes != null ? ` · ${formatBytes(sn.sizeBytes)}` : ""}</p>
                </div>
                {canManage && <button type="button" className={tabSecondaryClass} onClick={() => setRestoring(sn.id)}><RotateCcw size={14} />Restore</button>}
              </li>
            ))}
          </ul>
        )}
      <ConfirmDialog open={restoring !== null} onOpenChange={(o) => !o && setRestoring(null)} title="Restore this rollback point?"
        description={`The files and database of ${w.primaryDomain} go back to how they were at that moment. Changes made since then are lost.`}
        confirmLabel="Restore" confirmText={w.primaryDomain} loading={restore.isPending} error={restore.error} onConfirm={() => restoring && restore.mutate(restoring)} />
    </TabCard>
  );
}
